/*
 * -----------------------------------------------------------------------
 *  <copyright file="custom-column-menu.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import {
    GridColumnMenuSort,
    GridColumnMenuFilter,
    GridColumnMenuItemGroup,
    GridColumnMenuItem,
    GridColumnMenuItemContent,
} from '@progress/kendo-react-grid';
import React, { useState } from 'react';

import { useBoolean, useTranslation } from 'hooks';

import { IExpandableGridColumn } from 'views/components/core/expandable/grid/interfaces/grid-props.interface';
import { GridColumnMenuBooleanFilter, GridColumnDateFilter } from 'views/components/core/grid-filters';

import './styles.scss';

const CustomColumnMenu = (props: any) => {
    const { t } = useTranslation();

    const columnExpanded = useBoolean(false);
    const filterExpanded = useBoolean(false);
    const lockExpanded = useBoolean(false);
    const initialArray = [...props.columns].map((item: any) => ({ ...item }));
    let array = [...initialArray].map((item: any) => {
        // show not only parent columns but the child as well
        if (item.children) {
            item.children = item.children.map((el: any) => ({ ...el, children: true, parent: item.id }));
            return [...item.children];
        }
        return item;
    });
    // flat this array to one dimensional
    array = array.reduce((acc, val) => acc.concat(val), []);

    const [columnsArrayShow, setColumnsArrayShow] = useState([...array]);
    const [columnsArray, setColumnsArray] = useState([...props.columns]);

    const onFilterExpandChange = (value: boolean) => {
        columnExpanded.setValue(value ? false : columnExpanded.value);
        lockExpanded.setValue(value ? false : lockExpanded.value);
        filterExpanded.setValue(value);
    };

    const onMenuItemClick = () => {
        const value = !columnExpanded.value;
        columnExpanded.setValue(value);
        filterExpanded.setValue(value ? false : filterExpanded.value);
        lockExpanded.setValue(value ? false : lockExpanded.value);
    };

    const onLockMenuClick = () => {
        const value = !lockExpanded.value;
        lockExpanded.setValue(value);
        columnExpanded.setValue(value ? false : filterExpanded.value);
        filterExpanded.setValue(value ? false : filterExpanded.value);
    };

    const onSubmit = (key: string) => (event: any) => {
        if (event) event.preventDefault();
        if (props.gridKey) {
            if (key === 'lock') {
                props.onColumnsSubmit(columnsArray);
            } else {
                const result = props.columns.map((item: any) => {
                    if (item.children) {
                        item.children = item.children.map((el: any) => {
                            // find column which you want to hide
                            const children = columnsArrayShow.find((element: any) => element.id === el.id);
                            return {
                                ...el,
                                show: children.show,
                            };
                        });
                        const visibleChildren = item.children.filter((el: any) => el.show === true);

                        return {
                            ...item,
                            minWidth: visibleChildren.reduce((sum: number, item: any) => sum + item.minWidth, 0),
                            show: item.children.length - visibleChildren.length !== item.children.length,
                        };
                    }
                    // find column which you want to hide
                    const element = columnsArrayShow.find((el: any) => item.id === el.id);
                    return {
                        ...item,
                        show: element.show,
                    };
                });
                props.onColumnsSubmit(result);
            }
        }
        if (props.onCloseMenu) props.onCloseMenu();
    };

    const onReset = (key: string) => (event: any) => {
        event.preventDefault();
        let allColumns: any = [];
        let filteredArray: any = [];
        if (key === 'lock') {
            allColumns = columnsArray.map((col: any) => {
                if (col.children && col.children.length)
                    col.children = col.children.map((item: any) => ({
                        ...item,
                        locked: false,
                    }));
                return {
                    ...col,
                    locked: col.lockedByDefault !== undefined ? col.lockedByDefault : false,
                };
            });

            filteredArray = allColumns
                .filter((column: IExpandableGridColumn) => column.locked)
                .map((column: IExpandableGridColumn) => column.field);
            setColumnsArray(allColumns);
        } else {
            allColumns = [...props.columns].map((col: any) => {
                if (col.children && col.children.length)
                    col.children = col.children.map((item: any) => ({
                        ...item,
                        show: col.showByDefault !== undefined ? col.showByDefault : true,
                    }));
                return {
                    ...col,
                    show: col.showByDefault !== undefined ? col.showByDefault : true,
                };
            });
            setColumnsArrayShow(allColumns);
        }
        props.onColumnsSubmit(allColumns);
    };

    const onToggleColumn = (id: number, key: string) => {
        if (key === 'show')
            setColumnsArrayShow(
                columnsArrayShow.map((column: IExpandableGridColumn, idx: number) => {
                    if (idx === id)
                        return {
                            ...column,
                            show: !column.show,
                        };
                    return column;
                }),
            );
        else if (key === 'lock')
            setColumnsArray(
                columnsArray.map((column: any, idx: any) => {
                    if (idx === id) {
                        if (column.children && column.children.length) {
                            column.children = column.children.map((item: any) => ({ ...item, locked: !item.locked }));
                        }
                        return { ...column, locked: !column.locked };
                    }
                    return column;
                }),
            );
    };

    const oneVisibleColumn =
        columnsArray && columnsArray.length && columnsArray.filter((c: any) => c.show).length === 1;

    return (
        <div className="grid-column-menu-filter">
            <GridColumnMenuSort {...props} />
            <GridColumnMenuFilter
                {...props}
                onExpandChange={onFilterExpandChange}
                expanded={filterExpanded.value}
                filterUI={
                    props.filterUi ||
                    (props.column.filter === 'boolean'
                        ? GridColumnMenuBooleanFilter
                        : props.column.filter === 'date'
                        ? GridColumnDateFilter
                        : undefined)
                }
                filter={props.filter}
                column={{
                    ...props.column,
                    field: props.filterKey || props.column.field,
                }}
                onFilterChange={(filter: any, syntheticEvent: any) => {
                    if (props.filterKey && filter) {
                        const newFilter = { ...filter };
                        const valueFilter = [...newFilter.filters].map((item: any) => {
                            item.filters = [...item.filters].map((el: any) => {
                                let newField = el.field;
                                if (props.filterKey && props.column.field === el.field) newField = props.filterKey;
                                return {
                                    ...el,
                                    field: newField,
                                };
                            });
                            return item;
                        });
                        props.onFilterChange({
                            ...filter,
                            filters: valueFilter,
                        });
                    } else props.onFilterChange(filter);
                }}
            />
            <GridColumnMenuItemGroup>
                <GridColumnMenuItem title={t('lockColumns')} iconClass="k-i-lock" onClick={onLockMenuClick} />
                <GridColumnMenuItemContent show={lockExpanded.value}>
                    <div className="k-column-list-wrapper">
                        <form className="k-lock-menu" onSubmit={onSubmit('lock')} onReset={onReset('lock')}>
                            <div className="k-column-list">
                                {columnsArray.map((column: any, idx: any) => (
                                    <div
                                        key={idx}
                                        className="k-column-list-item"
                                        onClick={() => {
                                            onToggleColumn(idx, 'lock');
                                        }}
                                    >
                                        <input
                                            id={`column-visiblity-show-${idx}`}
                                            className="k-checkbox lock-checkbox"
                                            type="checkbox"
                                            readOnly
                                            disabled={!column.show}
                                            checked={column.locked}
                                        />
                                        <label htmlFor={`column-visiblity-lock-${idx}`} className="k-checkbox-label">
                                            {column.label || column.title}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <div className="k-columnmenu-actions">
                                <button type="reset" className="k-button">
                                    {t('reset')}
                                </button>
                                <button className="k-button k-primary">{t('save')}</button>
                            </div>
                        </form>
                    </div>
                </GridColumnMenuItemContent>
            </GridColumnMenuItemGroup>
            {!props.removeHideFilter ? (
                <GridColumnMenuItemGroup>
                    <GridColumnMenuItem title={t('columns')} iconClass="k-i-columns" onClick={onMenuItemClick} />
                    <GridColumnMenuItemContent show={columnExpanded.value}>
                        <div className="k-column-list-wrapper">
                            <form className="k-lock-menu" onSubmit={onSubmit('show')} onReset={onReset('show')}>
                                <div className="k-column-list">
                                    {columnsArrayShow.length &&
                                        columnsArrayShow.map((column: any, idx: number) => (
                                            <div key={idx} className="k-column-list-item">
                                                <input
                                                    id={`column-visiblity-show-${idx}`}
                                                    className="k-checkbox lock-checkbox"
                                                    type="checkbox"
                                                    readOnly
                                                    disabled={column.show && oneVisibleColumn}
                                                    checked={column.show}
                                                    onClick={() => {
                                                        onToggleColumn(idx, 'show');
                                                    }}
                                                />
                                                <label
                                                    htmlFor={`column-visiblity-show-${idx}`}
                                                    className="k-checkbox-label"
                                                >
                                                    {column.label || column.title}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                                <div className="k-columnmenu-actions">
                                    <button type="reset" className="k-button">
                                        {t('reset')}
                                    </button>
                                    <button className="k-button k-primary">{t('save')}</button>
                                </div>
                            </form>
                        </div>
                    </GridColumnMenuItemContent>
                </GridColumnMenuItemGroup>
            ) : null}
        </div>
    );
};

export default CustomColumnMenu;
